import { Form } from 'antd';
import Text from 'components/Text/Text';
import {
	IWithAdditionalValidationContext,
	WithAdditionalValidationContext,
} from 'components/WithAdditionalValidation/withAdditionalValidationContext';

import React, { useContext, useEffect } from 'react';
import { EditTeaserContext, IEditTeaserContext } from '../../../../../editTeaserState/editTeaserContext';
import AdditionalFields from './components/AdditionalFields/AdditionalFields';
import BtnBlock from './components/BtnBlock';
import ContentTypeSelect from './components/ContentTypeSelect';
import CustomerSelect from './components/CustomerSelect';
import DescriptionField from './components/DescriptionField';
import RestaurantsField from './components/RestaurantsField/RestaurantsField';
import { getFileIdForSubmit, preventDefaultForEnterKey } from 'utils/helpers';
import style from './InputFields.module.scss';

const InputFields: React.FC = () => {
	const {
		changeTypeOfModalWindow,
		setDataForSubmit,
		setCustomer,
		setFetchedCustomers,
		setRestaurants,
		setFetchedRestaurants,
		setContentType,
		changeDescription,
		changeSchedule,
		changeAdditionalSchedule,
		setSchedules,
		setTransitionType,
		setTransitionDishCategory,
		setFetchedDishCategories,
		setTransitionDishSubCategory,
		setFetchedDishSubCategories,
		setTransitionDishName,
		setFetchedDishes,
		setTransitionGameCategory,
		setFetchedGameCategories,
		setTransitionGameName,
		setFetchedGames,
		setControlPoint,
		changeDelayTime,
		changeLaunchCount,
		changeCountDown,
		teaserID,
		fileId,
		filePath,
		selectedCustomer,
		selectedRestaurants,
		fetchedRestaurants,
		selectedContentType,
		description,
		schedule,
		totalSchedules,
		selectedTransitionType,
		dishTransition,
		selectedTransitionDishCategory,
		selectedTransitionDishSubCategory,
		selectedTransitionDishName,
		selectedTransitionGameCategory,
		selectedTransitionGameName,
		selectedControlPoint,
		delayTime,
		launchCount,
		countDown,
	} = useContext<IEditTeaserContext>(EditTeaserContext);
	const { setMainFormValidationFail, additionalValidationErrors } = useContext<IWithAdditionalValidationContext>(
		WithAdditionalValidationContext,
	);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			selectedCustomer,
			selectedRestaurants,
			selectedContentType,
			description,
			schedule,
			selectedTransitionType,
			selectedTransitionDishCategory,
			selectedTransitionDishSubCategory,
			selectedTransitionDishName,
			selectedTransitionGameCategory,
			selectedTransitionGameName,
			selectedControlPoint,
			delayTime,
			launchCount,
			countDown,
		});
		totalSchedules.map((el) => {
			form.setFieldsValue({ [`additionalSchedule-${el.id}`]: el.value });
		});
	}, [
		selectedCustomer,
		selectedRestaurants,
		selectedContentType,
		description,
		schedule,
		totalSchedules,
		selectedTransitionType,
		selectedTransitionDishCategory,
		selectedTransitionDishSubCategory,
		selectedTransitionDishName,
		selectedTransitionGameCategory,
		selectedTransitionGameName,
		selectedControlPoint,
		delayTime,
		launchCount,
		countDown,
	]);

	const submit = async () => {
		setMainFormValidationFail(false);

		const finalSchedule = (): string[] | null => {
			if (!schedule && !totalSchedules.length) {
				return null;
			}
			return [schedule, ...totalSchedules.map((el) => el.value)];
		};

		const dishTransitionData = {
			categoryId: selectedTransitionDishCategory!,
			subCategoryId: selectedTransitionDishSubCategory,
			dishId: selectedTransitionDishName,
		};

		const gameTransitionData = {
			gameCategoryId: selectedTransitionGameCategory,
			gameId: selectedTransitionGameName,
		};

		setDataForSubmit({
			id: teaserID,
			customerId: selectedCustomer || '',
			restaurantIds: selectedRestaurants,
			fileId: getFileIdForSubmit({ fileId, filePath }),
			description,
			contentType: selectedContentType!,
			controlPoint: selectedControlPoint,
			launchCount: launchCount ? Number(launchCount) : null,
			delayTime: delayTime ? Number(delayTime) : null,
			countDown: countDown ? Number(countDown) : null,
			schedule: finalSchedule(),
			dishTransition: selectedTransitionType === 'dish' ? dishTransitionData : null,
			gameLaunchTransition: selectedTransitionType === 'game' ? gameTransitionData : null,
		});
		additionalValidationErrors.length
			? setMainFormValidationFail(true)
			: changeTypeOfModalWindow('toConfirmSubmit');
	};

	const handleFormValidationFail = () => {
		setMainFormValidationFail(true);
	};

	return (
		<div className={style.container} data-test="InputFields">
			<Form
				form={form}
				name="basic"
				onFinish={submit}
				onKeyPress={preventDefaultForEnterKey}
				onFinishFailed={handleFormValidationFail}
				layout="vertical">
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="mainInfoHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<CustomerSelect setCustomer={setCustomer} setFetchedCustomers={setFetchedCustomers} />
					<RestaurantsField
						selectedRestaurants={selectedRestaurants}
						fetchedRestaurants={fetchedRestaurants}
						selectedCustomer={selectedCustomer}
						setRestaurants={setRestaurants}
						setFetchedRestaurants={setFetchedRestaurants}
					/>
					<ContentTypeSelect selectedContentType={selectedContentType} setContentType={setContentType} />
					<DescriptionField changeDescription={changeDescription} />
				</div>
				<AdditionalFields
					selectedContentType={selectedContentType}
					totalSchedules={totalSchedules}
					selectedRestaurants={selectedRestaurants}
					selectedTransitionDishCategory={selectedTransitionDishCategory}
					selectedTransitionDishSubCategory={selectedTransitionDishSubCategory}
					selectedTransitionGameCategory={selectedTransitionGameCategory}
					selectedTransitionType={selectedTransitionType}
					changeAdditionalSchedule={changeAdditionalSchedule}
					changeSchedule={changeSchedule}
					changeLaunchCount={changeLaunchCount}
					setSchedules={setSchedules}
					changeCountDown={changeCountDown}
					setControlPoint={setControlPoint}
					changeDelayTime={changeDelayTime}
					setTransitionType={setTransitionType}
					setTransitionDishCategory={setTransitionDishCategory}
					setFetchedDishCategories={setFetchedDishCategories}
					setFetchedDishSubCategories={setFetchedDishSubCategories}
					setTransitionDishSubCategory={setTransitionDishSubCategory}
					setTransitionDishName={setTransitionDishName}
					setFetchedDishes={setFetchedDishes}
					setFetchedGameCategories={setFetchedGameCategories}
					setTransitionGameCategory={setTransitionGameCategory}
					setTransitionGameName={setTransitionGameName}
					setFetchedGames={setFetchedGames}
					dishTransition={dishTransition}
					selectedTransitionDishName={selectedTransitionDishName}
				/>
				<BtnBlock />
			</Form>
		</div>
	);
};

export default InputFields;
